import React, { useRef, useState } from 'react';
import { Archive, Building2, Check, ChevronDown, ChevronUp, Download, FileText, HelpCircle, Link2, Loader2, PackageCheck, Share2, ShieldCheck, Trash2, TriangleAlert, Upload, UploadCloud, X } from 'lucide-react';
import { Confidence, ConfidenceTag } from '../ui/ConfidenceTag';
type DocumentStatus = 'uploaded' | 'missing' | 'needs-review';
type Requirement = {
  name: string;
  status: DocumentStatus;
  provided: string;
  uploadedAt?: string;
  filename?: string;
  note?: string;
};
type RequirementCategory = {
  id: string;
  title: string;
  missing: number;
  icon: typeof ShieldCheck;
  documents: Requirement[];
};
const categories: RequirementCategory[] = [{
  id: 'safety',
  title: 'Product Safety & Quality',
  missing: 4,
  icon: ShieldCheck,
  documents: [{
    name: 'Product Formulation',
    status: 'needs-review',
    uploadedAt: '26/07/2026',
    filename: 'Orange_Immunity_Powder_Formulation.xlsx',
    note: 'Vitamin A content exceeds the advisory statement threshold — see Formula.',
    provided: 'Full product formulation: product name and code, every active and excipient with quantity per unit dose, dosage form and batch size.'
  }, {
    name: 'Raw Material Certificate of Analysis (CoA)',
    status: 'missing',
    provided: 'Certificate of Analysis for each incoming raw-material batch, including ingredient identity, batch number, expiry or retest date and authorised signatory.'
  }, {
    name: 'Finished Product CoA',
    status: 'missing',
    provided: 'Certificate of Analysis for a finished-product batch, including product name, batch number, expiry date, assay results and pass/fail conclusion.'
  }, {
    name: 'Heavy Metals Testing',
    status: 'missing',
    provided: 'Heavy-metals test results: lead, arsenic, cadmium and mercury with units, method, and pass/fail against the elemental impurity limits of the applicable default standard.'
  }, {
    name: 'Microbiological Specifications',
    status: 'missing',
    provided: 'Microbiological specifications and limits: total aerobic count, yeast and mould, pathogen absence requirements, test method and pass/fail conclusion.'
  }]
}, {
  id: 'labelling',
  title: 'Labelling & Artwork',
  missing: 1,
  icon: FileText,
  documents: [{
    name: 'Label Artwork (Panel included)',
    status: 'uploaded',
    uploadedAt: '26/07/2026',
    filename: 'Orange_15ct_Carton_R4.pdf',
    provided: 'Consumer label artwork including product name, active ingredient list with quantities, nutrition information panel, country of origin, directions for use, warning statements and claims.'
  }, {
    name: 'Claims Review',
    status: 'missing',
    provided: 'Claims review: full list of product claims, their permitted standard, evidence basis and permitted or not-permitted outcome.'
  }]
}, {
  id: 'packaging',
  title: 'Packaging & Sustainability',
  missing: 2,
  icon: PackageCheck,
  documents: [{
    name: 'Container & Closure Compatibility',
    status: 'missing',
    provided: 'Compatibility evidence showing the selected container and closure protect the product through its declared shelf life.'
  }, {
    name: 'Packaging Specifications',
    status: 'missing',
    provided: 'Packaging specification: primary material type and grade, supplier, dimensions and fill volume, closure type and product compatibility.'
  }]
}, {
  id: 'supplier',
  title: 'Supplier & Manufacturing',
  missing: 3,
  icon: Building2,
  documents: [{
    name: 'Manufacturing Flowchart',
    status: 'missing',
    provided: 'A complete manufacturing flowchart showing receipt, weighing, blending, filling, packaging and quality-control checkpoints.'
  }, {
    name: 'GMP Certification',
    status: 'missing',
    provided: 'Current GMP certificate for the finished-product manufacturing facility and scope of certification.'
  }, {
    name: 'Manufacturer Agreement',
    status: 'missing',
    provided: 'Executed agreement defining quality responsibilities, release authority, change control and complaint handling.'
  }]
}];
interface ProductDossierTabProps {
  onScanLabel: () => void;
  productName?: string;
  isSingapore?: boolean;
  isItaly?: boolean;
  onCoaUploaded?: () => void;
}
type CoaStage = 'idle' | 'reading' | 'classifying' | 'done';
export function ProductDossierTab({
  onScanLabel,
  productName = 'Orange Immunity Powder',
  isSingapore = false,
  isItaly = false,
  onCoaUploaded
}: ProductDossierTabProps) {
  const isFood = productName === 'Chocolate Powdered Drink';
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({
    safety: true,
    labelling: true
  });
  const toggleCategory = (id: string) => setOpenCategories(open => ({
    ...open,
    [id]: !open[id]
  }));
  const [coaStage, setCoaStage] = useState<CoaStage>('idle');
  const [toastVisible, setToastVisible] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const coaComplete = coaStage === 'done';
  const handleFile = () => {
    setCoaStage('reading');
    window.setTimeout(() => setCoaStage('classifying'), 1500);
    window.setTimeout(() => {
      setCoaStage('done');
      onCoaUploaded?.();
      setToastVisible(true);
      window.setTimeout(() => setToastVisible(false), 5000);
    }, 3000);
  };
  const visibleCategories = isItaly ? categories.map(category => ({
    ...category,
    missing: category.documents.length,
    documents: category.documents.map(document => ({
      ...document,
      status: 'missing' as DocumentStatus,
      filename: undefined,
      uploadedAt: undefined,
      note: undefined
    }))
  })) : isFood ? categories.map(category => ({
    ...category,
    documents: category.documents.map(document => document.name === 'Product Formulation' ? {
      ...document,
      filename: undefined,
      note: undefined
    } : document)
  })) : categories;
  return <div className="space-y-6" data-id="element-2140" data-mp="2zd7om6akmhvj">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-2141" data-mp="2ug8xlwm7er1k">
        <div data-id="element-2142" data-mp="sqtahcz697sz">
          <h2 className="font-[Figtree] text-2xl font-semibold tracking-tight text-evergreen" data-id="element-2143" data-mp="sqa5ewumedwd">{productName}</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#6d7e71]" data-id="element-2144" data-mp="xitgnn5u4wy0">Baseline compliance dossier for each target market — upload documents to build it.</p>
        </div>
        <button type="button" onClick={onScanLabel} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-lime-glow px-4 py-3 text-sm font-semibold text-[#182d22] shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-forest-green focus:ring-offset-2" data-id="element-2145" data-mp="nrgr527ieiv5">
          <UploadCloud className="h-4 w-4" data-id="element-2146" data-mp="12g34wzhhn2k9" />
          Scan a label
        </button>
      </div>

      <section className="max-w-2xl rounded-2xl border-2 border-forest-green bg-[#f2fbf4] p-5 shadow-sm" aria-label="Selected market" data-id="element-2147" data-mp="16hoof3oj2f3f">
        <div className="flex items-start justify-between gap-4" data-id="element-2148" data-mp="3n629cyzbqti4">
          <div data-id="element-2149" data-mp="3ra07b6cwxy9l">
            <p className="flex items-center gap-2 font-[Figtree] text-lg font-semibold text-forest-green" data-id="element-2150" data-mp="3raieuz50zywt"><span className="text-xl" aria-hidden="true" data-id="element-2151" data-mp="3jo4pzcdlti1s">{isItaly ? '🇮🇹' : isSingapore ? '🇸🇬' : '🇦🇺'}</span>{isItaly ? 'Italy' : isSingapore ? 'Singapore (HSA)' : isFood ? 'Australia (FSANZ)' : 'Australia (TGA)'}</p>
            <p className="mt-3 text-sm text-[#5f7567]" data-id="element-2152" data-mp="3tmu9f9l5fak5">{isItaly ? 'Food Supplement (Integratore Alimentare)' : isSingapore ? 'Health Supplement' : isFood ? 'General Food' : 'Listed Complementary Medicine (AUST L)'}</p>
          </div>
          <RiskTag tone={isItaly ? 'medium' : 'low'} data-id="element-2153" data-mp="2w03o2maxkzvc" />
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-[#e1ece0]" aria-label={`${isItaly ? 0 : isSingapore ? 25 : 17}% complete`} data-id="element-2154" data-mp="2vxqybmyxll7q"><div className="h-full rounded-full bg-forest-green" style={{
          width: `${isItaly ? 0 : isSingapore ? 25 : 17}%`
        }} data-id="element-2155" data-mp="35yb2axdu53tn" /></div>
        <div className="mt-3 flex items-center justify-between font-mono text-xs font-semibold" data-id="element-2156" data-mp="2i7f51kqm1gf0"><span className="text-[#6b7d70]" data-id="element-2157" data-mp="2da5iqdyipkve">{isItaly ? 'Required 0/8' : isSingapore ? 'Required 2/8' : 'Required 2/12'}</span><span className="text-red-700" data-id="element-2158" data-mp="1rr2jqlb0kumk">{isItaly ? '8 missing' : isSingapore ? '6 missing' : '10 missing'}</span></div>
      </section>

      <section aria-labelledby="required-documents-heading" data-id="element-2159" data-mp="1wr873pa93uyu">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-2160" data-mp="21n5l3oy1kp8g">
          <h2 id="required-documents-heading" className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-2161" data-mp="25qv9adat14sy">Required documents · {isItaly ? 'Italy' : isSingapore ? 'Singapore (HSA)' : isFood ? 'Australia (FSANZ)' : 'Australia (TGA)'}</h2>
          <button type="button" className="inline-flex items-center justify-center gap-2 self-start rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-800 transition hover:bg-red-50 sm:self-auto" data-id="element-2162" data-mp="2flwz7bcwmawl"><Trash2 className="h-4 w-4" data-id="element-2163" data-mp="2tfehytxrnjlk" />Delete market dossier</button>
        </div>

        <div className="mt-4 rounded-2xl border border-[#dce3da] bg-white p-6 shadow-sm" data-id="element-2164" data-mp="2tdbjrmmgmk5r">
          <div className="flex items-center justify-between gap-4" data-id="element-2165" data-mp="2yer39a8zoim1">
            <span className="flex items-center gap-2 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-2166" data-mp="ms71dbufy66o"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#eff3ed] text-[#6e8173]" data-id="element-2167" data-mp="hqzwbkjonl3p"><Archive className="h-5 w-5" data-id="element-2168" data-mp="drhb0nny980c" /></span>Documents</span>
            <span className="font-mono text-sm font-semibold text-[#6d7e71]" data-id="element-2169" data-mp="8rcw322owlc8">Uploaded {isItaly ? '0/8' : `${coaComplete ? 3 : 2}/12`}</span>
          </div>
          <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-[#edf1eb]" aria-label={`${isItaly ? 0 : 17}% documents complete`} data-id="element-2170" data-mp="15h40pu4p3ovr"><div className="h-full rounded-full bg-[#76867c]" style={{
            width: `${isItaly ? 0 : 17}%`
          }} data-id="element-2171" data-mp="11i4v07b53o39" /></div>
          <div className="mt-5 grid gap-6 md:grid-cols-2" data-id="element-2172" data-mp="rq8me2bfgfks">
            <div className="space-y-2 font-mono text-sm text-[#607266]" data-id="element-2173" data-mp="rppg5iv53fml">
              <StatLine label="Uploaded" value={isItaly ? '0' : coaComplete ? '3' : '2'} data-id="element-2174" data-mp="1p5ut1bw0ut8g" />
              <StatLine label="Expiring soon" value="0" data-id="element-2175" data-mp="1kecx528uviuc" />
              <StatLine label="Needs review" value={isItaly ? '0' : '1'} dot={isItaly ? undefined : 'bg-amber-500'} note={isItaly ? undefined : '(subset of uploaded)'} data-id="element-2176" data-mp="1kcte53vtetov" />
            </div>
            <div className="space-y-2 font-mono text-sm text-[#607266]" data-id="element-2177" data-mp="2u00dz1d60kdc">
              <StatLine label="Missing" value={isItaly ? '8' : '10'} dot="bg-red-600" data-id="element-2178" data-mp="37yqd0mjjou4c" />
              <StatLine label="Expired" value={!isItaly && coaComplete ? '1' : '0'} dot={!isItaly && coaComplete ? 'bg-red-600' : undefined} data-id="element-2179" data-mp="33w3pgxtwpvcy" />
              <StatLine label="Incomplete" value="0" data-id="element-2180" data-mp="3hu1tpaeikbeq" />
            </div>
          </div>
          {!isItaly && coaComplete && <p className="mt-4 text-xs text-[#8b968d]" data-id="element-2181" data-mp="3cv79c40weesi">Expired documents do not count toward completeness.</p>}
          <p className="mt-5 font-mono text-xs font-semibold text-[#718174]" data-id="element-2182" data-mp="3mmv1k75a3zv1">{isItaly ? '0% complete' : '17% complete'}</p>
        </div>
      </section>

      <section aria-labelledby="foundation-heading" data-id="element-2183" data-mp="4hro9z55shjj">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-2184" data-mp="3vnt4sn7w83ck">
          <h2 id="foundation-heading" className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-2185" data-mp="j46vu7ut2dhl">Quality &amp; Manufacturing Foundation</h2>
          <div className="flex flex-wrap gap-2" data-id="element-2186" data-mp="o1p3lux36mo0">
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-2187" data-mp="a38yg8evh47z"><Share2 className="h-4 w-4" data-id="element-2188" data-mp="e6oxtcibtiod" />Share documents</button>
            <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-2189" data-mp="buvtrrmjoam"><Link2 className="h-4 w-4" data-id="element-2190" data-mp="3mlp58jq87sk4" />Request documents</button>
          </div>
        </div>

        <div className="mt-4 space-y-4" data-id="element-2191" data-mp="3rks6ru6zzgnc">
          {visibleCategories.map(category => <CategoryCard key={category.id} category={category} open={Boolean(openCategories[category.id])} onToggle={() => toggleCategory(category.id)} coaStage={coaStage} onCoaUpload={() => fileInputRef.current?.click()} data-id="element-2192" data-mp="3djftjqj81t55" />)}
        </div>
      </section>

      <input ref={fileInputRef} type="file" className="hidden" onChange={event => {
      if (event.target.files?.length) handleFile();
      event.target.value = '';
    }} data-id="element-2193" data-mp="1o456ntocncfi" />

      {toastVisible && <div role="status" className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl border border-[#dce3da] bg-white px-4 py-3 shadow-xl" data-id="element-2194" data-mp="1edu0s1l3ut5z">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700" data-id="element-2195" data-mp="271g0uzv4jfo3"><TriangleAlert className="h-3.5 w-3.5" data-id="element-2196" data-mp="222v1t3rlz218" /></span>
          <p className="text-sm font-semibold text-evergreen" data-id="element-2197" data-mp="223ei6g1m1id5">Finished Product CoA uploaded — 1 issue found</p>
        </div>}
    </div>;
}
function RiskTag({
  tone = 'low'
}: {
  tone?: 'low' | 'medium';
}) {
  if (tone === 'medium') return <span className="rounded-full bg-amber-100 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-800" data-id="element-2198" data-mp="mluy6wdgks4d">Medium risk</span>;
  return <span className="rounded-full bg-forest-green/10 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-green" data-id="element-2199" data-mp="hl6y7l7950y0">Low risk</span>;
}
function StatLine({
  label,
  value,
  dot,
  note
}: {
  label: string;
  value: string;
  dot?: string;
  note?: string;
}) {
  return <div className="flex items-center justify-between gap-3" data-id="element-2200" data-mp="3298hd5ay1l3n"><span className="inline-flex items-center gap-1.5" data-id="element-2201" data-mp="3721r67l9rjml">{dot && <span className={`h-2 w-2 rounded-full ${dot}`} data-id="element-2202" data-mp="373mkxuzreyje" />}{label}{note && <span className="text-xs text-[#9ba79e]" data-id="element-2203" data-mp="3bztespevsjkz">{note}</span>}</span><span className="font-semibold text-evergreen" data-id="element-2204" data-mp="3gzfud5i6mbwy">{value}</span></div>;
}
function CategoryCard({
  category,
  open,
  onToggle,
  coaStage = 'idle',
  onCoaUpload
}: {
  category: RequirementCategory;
  open: boolean;
  onToggle: () => void;
  coaStage?: CoaStage;
  onCoaUpload?: () => void;
}) {
  const Icon = category.icon;
  return <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-2205" data-mp="1rm2wnz4krm4t">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-[#f8faf7] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest-green" data-id="element-2206" data-mp="1vneg1mwuvb4k">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-[#6f8174]" data-id="element-2207" data-mp="1aa43pftnhn30"><Icon className="h-5 w-5" data-id="element-2208" data-mp="15bj51ku1rbgo" /></span>
        <span className="min-w-0 flex-1" data-id="element-2209" data-mp="15bsv18f3dimd"><span className="block font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2210" data-mp="159ggz6cgfsel">{category.title}</span><span className="mt-0.5 block font-mono text-xs text-[#718174]" data-id="element-2211" data-mp="hjc9gnw5b8ib">{category.missing} missing</span></span>
        {open ? <ChevronUp className="h-5 w-5 text-[#718174]" data-id="element-2212" data-mp="rb8defkwxsug" /> : <ChevronDown className="h-5 w-5 text-[#718174]" data-id="element-2213" data-mp="rd2zhx7x7r04" />}
      </button>
      {open && <div className="border-t border-[#e5ebe3] px-5" data-id="element-2214" data-mp="mk019bs77tdl">{category.documents.map(document => {
        if (document.name === 'Finished Product CoA' && coaStage !== 'idle') {
          if (coaStage === 'done') return <CoaResultRow key={document.name} data-id="element-2215" data-mp="3q0p5us880t0m" />;
          return <CoaProcessingRow key={document.name} stage={coaStage} data-id="element-2216" data-mp="25jpst63n8mt7" />;
        }
        return <RequirementRow key={document.name} document={document} onUpload={document.name === 'Finished Product CoA' ? onCoaUpload : undefined} data-id="element-2217" data-mp="25hmxc6jq21h3" />;
      })}</div>}
    </section>;
}
const coaValidations = [{
  tone: 'fail' as const,
  name: 'Document within its validity period',
  detail: 'This certificate expired on 17 April 2022, 1,570 days ago. A finished product CoA must be current for the batch being supplied.'
}, {
  tone: 'pass' as const,
  name: 'Product identity matches this dossier',
  detail: "Document product name 'Orange Immunity Powder 15ct' matches the dossier product."
}, {
  tone: 'query' as const,
  name: 'Issuer recognised',
  detail: 'TCI Co., Ltd. is not on your approved supplier list. Confirm the issuer or add them.',
  actions: ['Confirm issuer', 'Add to suppliers']
}];
const coaExtracted: {
  field: string;
  value: string;
  confidence: Confidence;
}[] = [{
  field: 'Document type',
  value: 'Finished Product CoA',
  confidence: 'HIGH'
}, {
  field: 'Product',
  value: 'Orange Immunity Powder 15ct',
  confidence: 'HIGH'
}, {
  field: 'Batch number',
  value: 'TCI-2104-887',
  confidence: 'HIGH'
}, {
  field: 'Date of manufacture',
  value: '17/04/2020',
  confidence: 'HIGH'
}, {
  field: 'Expiry / retest date',
  value: '17/04/2022',
  confidence: 'HIGH'
}, {
  field: 'Issuer',
  value: 'TCI Co., Ltd.',
  confidence: 'MEDIUM'
}, {
  field: 'Authorised signatory',
  value: 'QA Manager',
  confidence: 'MEDIUM'
}, {
  field: 'Assay — vitamin A',
  value: '3,640 µg RE',
  confidence: 'HIGH'
}, {
  field: 'Assay — vitamin C',
  value: '558 mg',
  confidence: 'HIGH'
}, {
  field: 'Assay — zinc',
  value: '15.1 mg',
  confidence: 'HIGH'
}];
function CoaResultRow() {
  const [showWhy, setShowWhy] = useState(false);
  const [showValidations, setShowValidations] = useState(true);
  const [showExtracted, setShowExtracted] = useState(false);
  return <article className="flex flex-col gap-3 border-b border-[#edf0eb] py-5 last:border-b-0 lg:flex-row lg:items-start" data-id="element-2218" data-mp="c1s88rpf5qyg">
      <div className="flex min-w-0 flex-1 gap-3" data-id="element-2219" data-mp="1txu93dh3p82m">
        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-600 text-white" data-id="element-2220" data-mp="1yx6x41jpkl6s"><X className="h-3 w-3" data-id="element-2221" data-mp="23nnaxmrfy8to" /></span>
        <div className="min-w-0 flex-1" data-id="element-2222" data-mp="23oowil632dpn">
          <div className="flex flex-wrap items-center gap-2" data-id="element-2223" data-mp="27pqw533wwrs5">
            <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2224" data-mp="27oz1pvm1x03g">Finished Product CoA</h3>
            <span className="rounded-full border border-red-300 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-red-700" data-id="element-2225" data-mp="1b1ko067s7h94">Expired</span>
          </div>
          <div className="mt-3 max-w-2xl" data-id="element-2226" data-mp="1jymn61waw53w">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#819083]" data-id="element-2227" data-mp="1oz95r56jy89y">What to provide</p>
            <p className="mt-1 text-sm leading-relaxed text-[#687a6d]" data-id="element-2228" data-mp="2vg4fm58flib4">Certificate of Analysis for a finished-product batch, including product name, batch number, expiry date, assay results and pass/fail conclusion.</p>
          </div>
          <p className="mt-3 font-mono text-xs text-[#718174]" data-id="element-2229" data-mp="2c240riy1on0s">Uploaded: 03/08/2026 · Expires: 17/04/2022 <span className="font-semibold text-red-700" data-id="element-2230" data-mp="274casugynaz8">(−1,570 days)</span> · Issuer: TCI Co., Ltd. QA Manager</p>
          <button type="button" onClick={() => setShowWhy(visible => !visible)} aria-expanded={showWhy} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#637667] hover:text-forest-green hover:underline" data-id="element-2231" data-mp="2kwac4j4fk2ku"><ChevronDown className={`h-3.5 w-3.5 transition ${showWhy ? 'rotate-180' : ''}`} data-id="element-2232" data-mp="2pt952yccy3hg" />Why this document matters</button>
          {showWhy && <p className="mt-2 max-w-2xl rounded-lg bg-[#f4f7f2] p-3 text-sm leading-relaxed text-[#596c5e]" data-id="element-2233" data-mp="33s0g2bvxb91d">This evidence helps demonstrate that the product is suitable for the selected pathway and that its quality can be verified if requested by the regulator.</p>}

          <div className="mt-4 max-w-2xl overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-2234" data-mp="15dfca19k6e4l">
            <button type="button" onClick={() => setShowValidations(open => !open)} aria-expanded={showValidations} className="flex w-full items-center gap-2 bg-[#f6f9f4] px-4 py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6f8174]" data-id="element-2235" data-mp="v9gzooh5sbi7">
              <ChevronDown className={`h-3.5 w-3.5 transition ${showValidations ? 'rotate-180' : ''}`} data-id="element-2236" data-mp="ljfdy3ca356g" />Validations
            </button>
            {showValidations && <ul className="divide-y divide-[#edf0eb]" data-id="element-2237" data-mp="bl86qc7sk1hn">
                {coaValidations.map(validation => <li key={validation.name} className="flex gap-3 px-4 py-3" data-id="element-2238" data-mp="gl443slzguo8">
                    <ValidationIcon tone={validation.tone} data-id="element-2239" data-mp="2ktcuogrhe2j" />
                    <div className="min-w-0" data-id="element-2240" data-mp="2mnvfw4k8u3g">
                      <p className="text-sm font-semibold text-evergreen" data-id="element-2241" data-mp="7jmrwlbp5d4t">{validation.name}</p>
                      <p className="mt-1 text-sm leading-relaxed text-[#617366]" data-id="element-2242" data-mp="1wz3zqrdspy5o">{validation.detail}</p>
                      {validation.actions && <div className="mt-2 flex flex-wrap gap-2" data-id="element-2243" data-mp="21ww0mawf4um0">
                          {validation.actions.map(action => <button key={action} type="button" className="rounded-lg border border-[#d6dfd3] bg-white px-2.5 py-1 text-xs font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-2244" data-mp="35h28as2bg4ob">{action}</button>)}
                        </div>}
                    </div>
                  </li>)}
              </ul>}
          </div>

          <div className="mt-3 max-w-2xl overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-2245" data-mp="30ja7f8jp187z">
            <button type="button" onClick={() => setShowExtracted(open => !open)} aria-expanded={showExtracted} className="flex w-full items-center gap-2 bg-[#f6f9f4] px-4 py-2.5 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6f8174]" data-id="element-2246" data-mp="3o94ibkkgcmpl">
              <ChevronDown className={`h-3.5 w-3.5 transition ${showExtracted ? 'rotate-180' : ''}`} data-id="element-2247" data-mp="3j8qigpt08j2n" />Extracted data
            </button>
            {showExtracted && <ul className="divide-y divide-[#edf0eb]" data-id="element-2248" data-mp="23v46avlzcxyg">
                {coaExtracted.map(row => <li key={row.field} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5" data-id="element-2249" data-mp="27vo1vuqwntih">
                    <span className="min-w-[10rem] font-mono text-[11px] font-semibold uppercase tracking-[0.11em] text-[#7a897d]" data-id="element-2250" data-mp="20cwwxlf7tfcf">{row.field}</span>
                    <span className="flex-1 text-sm text-evergreen" data-id="element-2251" data-mp="2a99p2ev43l52">{row.value}</span>
                    <ConfidenceTag level={row.confidence} data-id="element-2252" data-mp="259dpcrtg6fvb" />
                  </li>)}
              </ul>}
          </div>
        </div>
      </div>
      <div className="shrink-0 lg:pl-4" data-id="element-2253" data-mp="1hkudk2b6vkyh">
        <div className="flex flex-wrap justify-end gap-2" data-id="element-2254" data-mp="1cl9ii2z53wr1">
          <ActionButton icon={<Upload className="h-3.5 w-3.5" data-id="element-2256" data-mp="1cj54utbfvjdk" />} data-id="element-2255" data-mp="1cmstpf5xvhly">Replace</ActionButton>
          <ActionButton icon={<Download className="h-3.5 w-3.5" data-id="element-2258" data-mp="1rbgpnmjp5tcf" />} data-id="element-2257" data-mp="1raxhgxjumjbl">Download</ActionButton>
          <ActionButton icon={<div className="h-3.5 w-3.5" data-id="element-2260" data-mp="1mhuiub07sjoj" />} data-id="element-2259" data-mp="1mie1xv1lpe45">Document history</ActionButton>
          <ActionButton destructive icon={<Trash2 className="h-3.5 w-3.5" data-id="element-2262" data-mp="ys0c8b6hnrcq" />} data-id="element-2261" data-mp="yst9mljk54i7">Delete</ActionButton>
        </div>
      </div>
    </article>;
}
function ValidationIcon({
  tone
}: {
  tone: 'pass' | 'fail' | 'query';
}) {
  if (tone === 'fail') return <X className="mt-0.5 h-4 w-4 shrink-0 text-red-600" data-id="element-2263" data-mp="1jgt4wnxra0nx" />;
  if (tone === 'pass') return <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-green" data-id="element-2264" data-mp="1nixrj2psdx12" />;
  return <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" data-id="element-2265" data-mp="1sc8s4m409hkj" />;
}
function CoaProcessingRow({
  stage
}: {
  stage: 'reading' | 'classifying';
}) {
  return <article className="flex items-center gap-3 border-b border-[#edf0eb] py-5 last:border-b-0" data-id="element-2266" data-mp="1xc50exjqjv5w">
      <Loader2 className="h-5 w-5 shrink-0 animate-spin text-amber-500" data-id="element-2267" data-mp="10me3ysrvvxxg" />
      <div data-id="element-2268" data-mp="14lwovojpg90a">
        <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2269" data-mp="14npmkeccbiw5">Finished Product CoA</h3>
        <p className="mt-1 text-sm text-amber-800" data-id="element-2270" data-mp="19n2idol6w0av">{stage === 'reading' ? 'Reading document…' : 'Classifying and extracting…'}</p>
      </div>
    </article>;
}
function RequirementRow({
  document,
  onUpload
}: {
  document: Requirement;
  onUpload?: () => void;
}) {
  const [showWhy, setShowWhy] = useState(false);
  const isUploaded = document.status === 'uploaded' || document.status === 'needs-review';
  return <article className="flex flex-col gap-3 border-b border-[#edf0eb] py-5 last:border-b-0 lg:flex-row lg:items-center" data-id="element-2271" data-mp="9cjabjid42i4">
      <div className="flex min-w-0 flex-1 gap-3" data-id="element-2272" data-mp="4lazcj1dw92u">
        <StatusCircle status={document.status} data-id="element-2273" data-mp="4k00mxmspril" />
        <div className="min-w-0" data-id="element-2274" data-mp="3vsnl7bd7pap3">
          <div className="flex flex-wrap items-center gap-2" data-id="element-2275" data-mp="3vrktx2bz968n">
            <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2276" data-mp="3rr21b629fw5e">{document.name}</h3>
            {document.status !== 'uploaded' && <DocumentStatusChip status={document.status} data-id="element-2277" data-mp="3mput55w7guy7" />}
          </div>
          <div className="mt-3 max-w-2xl" data-id="element-2278" data-mp="3hte3g79cy8j0">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#819083]" data-id="element-2279" data-mp="3hufi0lpl1d5h">What to provide</p>
            <p className="mt-1 text-sm leading-relaxed text-[#687a6d]" data-id="element-2280" data-mp="1g0pxvr98r902">{document.provided}</p>
          </div>
          {document.uploadedAt && <p className="mt-3 font-mono text-xs text-[#718174]" data-id="element-2281" data-mp="1b2y750kc1sxg">{document.filename ? `${document.filename} · ` : ''}Uploaded: {document.uploadedAt}</p>}
          {document.note && <p className="mt-3 max-w-2xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium leading-relaxed text-amber-900" data-id="element-2282" data-mp="1o1e11u1ioew6">{document.note}</p>}
          <button type="button" onClick={() => setShowWhy(visible => !visible)} aria-expanded={showWhy} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#637667] hover:text-forest-green hover:underline" data-id="element-2283" data-mp="196hl0ungun8j"><ChevronDown className={`h-3.5 w-3.5 transition ${showWhy ? 'rotate-180' : ''}`} data-id="element-2284" data-mp="152tfehiea1px" />Why this document matters</button>
          {showWhy && <p className="mt-2 max-w-2xl rounded-lg bg-[#f4f7f2] p-3 text-sm leading-relaxed text-[#596c5e]" data-id="element-2285" data-mp="qeyisq19nd7k">This evidence helps demonstrate that the product is suitable for the selected pathway and that its quality can be verified if requested by the regulator.</p>}
        </div>
      </div>
      <div className="shrink-0 lg:pl-4" data-id="element-2286" data-mp="lf2h4xg1jxv6">
        {isUploaded ? <UploadedActions data-id="element-2287" data-mp="lgnawkuj7crz" /> : <button type="button" onClick={onUpload} className="inline-flex items-center gap-2 rounded-xl bg-evergreen px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-forest-green" data-id="element-2288" data-mp="2q8qc3itd05b"><Upload className="h-4 w-4" data-id="element-2289" data-mp="3tz60w4u9yqhi" />Upload</button>}
      </div>
    </article>;
}
function StatusCircle({
  status
}: {
  status: DocumentStatus;
}) {
  if (status === 'uploaded') return <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest-green text-white" data-id="element-2290" data-mp="6u83qd8sriug"><ShieldCheck className="h-3 w-3" data-id="element-2291" data-mp="ln1ly6fged2t" /></span>;
  if (status === 'needs-review') return <span className="mt-1 h-5 w-5 shrink-0 rounded-full border-2 border-amber-500 bg-amber-50" data-id="element-2292" data-mp="gq1g9wmincc8" />;
  return <span className="mt-1 h-5 w-5 shrink-0 rounded-full border-2 border-[#839589] bg-white" data-id="element-2293" data-mp="ujsngycryk4s" />;
}
function DocumentStatusChip({
  status
}: {
  status: Exclude<DocumentStatus, 'uploaded'>;
}) {
  const style = status === 'missing' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800';
  const label = status === 'missing' ? 'Missing' : 'Needs review';
  return <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] ${style}`} data-id="element-2294" data-mp="3talqxa5w6tn2">{label}</span>;
}
function UploadedActions() {
  return <div className="flex flex-wrap justify-end gap-2" data-id="element-2295" data-mp="639pqk7razgb">
      <ActionButton icon={<Upload className="h-3.5 w-3.5" data-id="element-2297" data-mp="av1ecp7cqv3t" />} data-id="element-2296" data-mp="auhyde19igsf">Replace</ActionButton>
      <ActionButton icon={<Download className="h-3.5 w-3.5" data-id="element-2299" data-mp="fue179y8426e" />} data-id="element-2298" data-mp="ftuukpe0x07o">Download</ActionButton>
      <ActionButton icon={<Archive className="h-3.5 w-3.5" data-id="element-2301" data-mp="ksxrtnnri253" />} data-id="element-2300" data-mp="fvyz99jqy390">Move</ActionButton>
      <ActionButton icon={<div className="h-3.5 w-3.5" data-id="element-2303" data-mp="kusbkynaro7l" />} data-id="element-2302" data-mp="kr4ghv27etqr">Document history</ActionButton>
      <ActionButton destructive icon={<Trash2 className="h-3.5 w-3.5" data-id="element-2305" data-mp="osq2c5wpuid3" />} data-id="element-2304" data-mp="ov2gs939male">Delete</ActionButton>
    </div>;
}
function ActionButton({
  children,
  icon,
  destructive = false
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  destructive?: boolean;
}) {
  return <button type="button" className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition ${destructive ? 'border-red-200 text-red-800 hover:bg-red-50' : 'border-[#d6dfd3] text-evergreen hover:bg-[#f7faf5]'}`} data-id="element-2306" data-mp="2vk7a3i6o5mgw">{icon}{children}</button>;
}